import CloseIcon from 'assets/images/closeIconGray.svg?react';
import SearchIcon from 'assets/images/searchIcon.svg?react';
import { EventState, GamesState } from 'features/eventPreMatch/interface/sportPrematch';
import { AnimatePresence, motion } from 'framer-motion';
import debounce from 'lodash/debounce';
import React, { ChangeEvent, SetStateAction, useCallback, useEffect, useRef } from 'react';
import { replacePolishCharacters } from '../../../../shared/util/replacePolishCharacters';
import { LiveDetails } from '../../../eventLive/interface/interface';

type SearchBarProps = {
	eventDetailData: EventState | LiveDetails;
	setFilteredList: (value: GamesState[]) => void;
	isExpanded: boolean;
	setIsExpanded: React.Dispatch<SetStateAction<boolean>>;
	value: string;
	setValue: React.Dispatch<SetStateAction<string>>;
	changeCurrentTab: (activePillTab: string) => void;
	activePillTab: string;
	isFootball?: boolean;
	filteredGames?: any;
	setFilteredGames?: any;
	groupedEvents: any;
};

type TOutcome = {
	outcomeId: number;
	outcomeName: string;
};

const SearchBar = ({
	eventDetailData,
	setFilteredList,
	isExpanded,
	setIsExpanded,
	value,
	setValue,
	changeCurrentTab,
	activePillTab,
	isFootball = false,
	filteredGames,
	setFilteredGames,
	groupedEvents,
}: SearchBarProps) => {
	const inputRef = useRef<HTMLInputElement>(null);

	const isLive = eventDetailData.isLive;

	const handleSearch = (inputValue: string) => {
		if (!isFootball || isLive) {
			const filteredGames = eventDetailData.games.filter((game) => {
				const gameTypeMatches = replacePolishCharacters(game?.gameName?.toString())?.includes(replacePolishCharacters(inputValue));
				const outcomeMatches = game.outcomes.some((outcome: TOutcome) =>
					replacePolishCharacters(outcome?.outcomeName)?.includes(replacePolishCharacters(inputValue)),
				);
				return gameTypeMatches || outcomeMatches;
			});

			setFilteredList(filteredGames);
			window.scroll(0, 0);
		} else {
			if (inputValue === '') {
				setFilteredGames(filteredGames);
				return;
			}
			const filteredItems = {} as any;
			//search for prematch for football only
			Object.keys(filteredGames).forEach((key) => {
				filteredGames[key].forEach((game: any) => {
					const gameNameMatches = replacePolishCharacters(game.gameName).includes(replacePolishCharacters(inputValue));
					if (gameNameMatches) {
						typeof filteredItems[key] === 'undefined' ? (filteredItems[key] = [game]) : filteredItems[key].push(game);
						return;
					}
					const outcomeMatches = game.outcomes.some((outcome: any) =>
						replacePolishCharacters(outcome.outcomeName).includes(replacePolishCharacters(inputValue)),
					);
					if (outcomeMatches) {
						filteredItems[key] = [game];
					}
				});
			});
			setFilteredGames(filteredItems);
		}
	};

	const debounceSearch = useCallback(debounce(handleSearch, 300), []);

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		setValue(event.target.value);
		debounceSearch(event.target.value);
	};

	useEffect(() => {
		if (isExpanded && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isExpanded]);

	const handleIconClick = () => {
		setIsExpanded(!isExpanded);
		activePillTab !== 'All' && changeCurrentTab('All');
	};

	const handleClose = () => {
		setFilteredList([]);
		setIsExpanded(false);
		setFilteredGames && setFilteredGames(groupedEvents);
		setValue('');
	};

	return (
		<div className="tab" onClick={handleIconClick}>
			<div className="search-bar-wrapper">
				<SearchIcon style={{ width: isExpanded ? '18px' : undefined, transition: 'width 0.3s' }} />
				<AnimatePresence>
					{isExpanded && (
						<motion.div
							className="search-bar"
							transition={{ duration: 0.2, ease: 'linear' }}
							initial={{ width: 0 }}
							animate={{ width: '300px' }}
							exit={{ width: 0, transition: { duration: 0.4, ease: 'linear' } }}
						>
							<motion.input
								onClick={(e) => {
									e.stopPropagation();
									if (isExpanded && inputRef.current) {
										inputRef.current.focus();
									}
								}}
								value={value}
								onChange={handleChange}
								className="search-bar-input"
								transition={{ duration: 0.1, ease: 'linear' }}
								ref={inputRef}
								type="text"
								initial={{ width: 0 }}
								animate={{ width: '260px' }}
								exit={{ width: 0, transition: { duration: 0.1, ease: 'linear' } }}
							/>
							<CloseIcon onClick={handleClose} />
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</div>
	);
};

export default SearchBar;
